import React from 'react';
import classNames from 'classnames';
import _ from 'lodash';
import { useBreakpointChange } from '_ui-core-js/React/hooks/useBreakpointChange.js';
import { useUpdateEffect } from '_ui-core-js/React/hooks/useUpdateEffect.js';
import { useSwipe } from '_ui-core-js/React/hooks/useSwipe.js';
import { publish } from '_ui-core-js/utilities/events.js';
import { CAROUSEL_ITEM_WIDTH_MAP } from './carouselItemWidthMap.js';
import { VehicleCard } from "../vehicle-card/VehicleCard.jsx";
import { useState, useRef, Fragment } from 'react';
import propTypes from 'prop-types';
import { fireGTMTrackingEvent } from '_ui-core-js/utilities/fireGTMTrackingEvent.js';
import { CORE_GTM_TRACKING_VALUES } from '_ui-core-js/constants/analytics.js';
import { RANGE_GTM_TRACKING_VALUES } from '../../../../../range/ui/constants/analytics.js';

export function RangeCarousel(props) {

  const { 
    children,
    showControls,
    activeSlideIndex,
    externalControl,
    zoomView,
    onMoveCallback,
    useChildPropForKey,
    resetDependencies,
    moveDelay
  } = props;

  const rootRef = useRef();
  const sliderRef = useRef();
  const [activeIndex, setActiveIndex] = useState(activeSlideIndex || 0);
  const [itemWidth, setItemWidth] = useState(useBreakpointChange(breakpoint => {
    setItemWidth(breakpoint);
  })[0]);
  const block = 'c-range-carousel';

  useUpdateEffect(() => {
    publish('range-carousel-move');
    setActiveIndex(0);
  }, [resetDependencies]);

  // Use effect when controlling carousel externally via 'activeIndex' prop.
  // Update the local state which tracks the active item and scrolls is into view.
  useUpdateEffect(() => {
    if (externalControl) {
      setTimeout(() => {
        setActiveIndex(activeSlideIndex);
      }, moveDelay);
    }
  }, [activeSlideIndex]);

  // Use effect after active index has been updated to scroll the current item into view.
  useUpdateEffect(() => {
    scrollTo(activeIndex);
  }, [activeIndex, itemWidth, zoomView]);

  function scrollTo(index) {
    sliderRef.current.style.transform = `translate3d(${(-1 * (index * CAROUSEL_ITEM_WIDTH_MAP[zoomView ? 'large' : 'small'][itemWidth])) + '%'}, 0, 0)`;
  };

  function getNextIndex(direction, currentIndex) {
    const offset = direction === 'next' ? 1 : -1;
    let nextIndex;
    if (currentIndex + offset === -1 || currentIndex + offset === sliderRef.current.children.length - 2) { // If on first/last item do nothing
      nextIndex = currentIndex;
    } else {
      nextIndex = currentIndex + offset;
    }
    return nextIndex;
  };

  function move(direction) {
    fireGTMTrackingEvent({
      name: CORE_GTM_TRACKING_VALUES.name.componentEvent,
      componentName: RANGE_GTM_TRACKING_VALUES.componentName.modelRange,
      componentGroup: CORE_GTM_TRACKING_VALUES.componentGroup.newCars,
      action: RANGE_GTM_TRACKING_VALUES.action.browseModel,
      label: ''
    })

    publish('range-carousel-move');
    if (externalControl && typeof onMoveCallback === 'function') {
      onMoveCallback(getNextIndex(direction, activeSlideIndex));
    } else {
      setActiveIndex(prevValue => getNextIndex(direction, prevValue));
    }
  };

  const swipeEvents = useSwipe((swipeRight, swipeLeft) => {
    if (swipeLeft) {
      move('prev');
    }
    else if (swipeRight) {
      move('next');
    }
  });

  const renderChildren = () => [undefined, ...children, undefined].map((child, index) => {
    
    return child ? 
      <div
        {...(useChildPropForKey ? { key: child.props[useChildPropForKey] } : {})}
        className={
          classNames({
            [`${block}__item`]: true,
            [`${block}__item--large`]: zoomView,
            [`${block}__item--active`]: index - 1 === activeIndex
          })}>
        {child}
      </div>
      :
      <div
        className={
          classNames({
            [`${block}__item`]: true,
            [`${block}__item--large`]: zoomView,
            [`${block}__item--placeholder`]: true
          })}>
      </div>
});

  return (

    <article ref={rootRef} className={block}>
      {showControls &&
        <Fragment>
          {
            activeIndex !== 0 &&
            <button
              className={
                classNames({
                  [`${block}__direction-button`]: true,
                  [`${block}__direction-button--left`]: true,
                  [`${block}__direction-button--large`]: zoomView
                })}
              onClick={() => move('prev')}
              aria-label={'back'}
            />
          }
          {
            activeIndex !== children.length - 1 &&
            <button
              className={
                classNames({
                  [`${block}__direction-button`]: true,
                  [`${block}__direction-button--right`]: true,
                  [`${block}__direction-button--large`]: zoomView
                })}
              onClick={() => move('next')}
              aria-label={'forward'}
              key={'forward-button'}
            />
          }
        </Fragment>
      }
      <div
        ref={sliderRef}
        className={
          classNames({
            [`${block}__slider`]: true,
            [`${block}__slider--small`]: !zoomView,
            [`${block}__slider--large`]: zoomView,
          })}
        { ...swipeEvents }
      >
        {renderChildren()}
      </div>
    </article>
  );
};

RangeCarousel.propTypes = {
  showControls: propTypes.bool,
  activeIndex: propTypes.number,
  externalControl: propTypes.bool,
  zoomView: propTypes.bool,
  onMoveCallback: propTypes.func,
  useChildPropForKey: propTypes.string,
  resetDependencies: propTypes.any,
  moveDelay: propTypes.number
};

RangeCarousel.defaultProps = {
  moveDelay: 0
};
